<template>
  <header class="nav" :class="{ 'nav--solid': scrolled || open, 'nav--en': lang === 'en' }">
    <div class="wrap nav__inner">
      <router-link :to="localePath('/')" class="nav__logo" @click="close" aria-label="牧色安全 Musesec">
        <BrandLogo />
      </router-link>

      <nav class="nav__links" aria-label="main">
        <div class="nav__drop" @mouseenter="drop = 'sol'" @mouseleave="drop = ''">
          <button class="nav__droptoggle" :aria-expanded="drop === 'sol'" @click="toggle('sol')">{{ c.solutionsNav }} <span class="caret">▾</span></button>
          <div class="nav__menu" v-show="drop === 'sol'">
            <router-link v-for="s in SOLUTIONS" :key="s.id" :to="localePath('/solutions/') + '#' + s.id" @click="drop = ''">{{ s[lang].name }}</router-link>
          </div>
        </div>
        <div class="nav__drop" @mouseenter="drop = 'svc'" @mouseleave="drop = ''">
          <button class="nav__droptoggle" :aria-expanded="drop === 'svc'" @click="toggle('svc')">{{ c.servicesNav }} <span class="caret">▾</span></button>
          <div class="nav__menu" v-show="drop === 'svc'">
            <router-link v-for="s in SERVICES" :key="s.slug" :to="localePath('/services/' + s.slug + '/')" @click="drop = ''">{{ s[lang].nav }}</router-link>
          </div>
        </div>
        <router-link v-for="l in links" :key="l.to" :to="l.to">{{ l.label }}</router-link>
      </nav>

      <div class="nav__actions">
        <button class="nav__lang" @click="switchLang">{{ c.nav.lang }}</button>
        <button class="btn btn--danger btn--sm nav__sos" @click="$emit('emergency')">
          <span class="sos-dot"></span>{{ c.nav.emergency }}
        </button>
        <router-link :to="localePath('/') + '#contact'" class="btn btn--primary btn--sm nav__cta" @click="track('click_cta', { label: 'nav' })">{{ c.nav.contact }}</router-link>
        <button class="nav__burger" :class="{ 'is-open': open }" @click="open = !open" aria-label="menu">
          <span></span><span></span>
        </button>
      </div>
    </div>

    <div class="nav__drawer" v-show="open">
      <p class="nav__drawer-h">{{ c.solutionsNav }}</p>
      <div class="nav__drawer-grid">
        <router-link v-for="s in SOLUTIONS" :key="s.id" :to="localePath('/solutions/') + '#' + s.id" @click="close">{{ s[lang].name }}</router-link>
      </div>
      <p class="nav__drawer-h">{{ c.servicesNav }}</p>
      <router-link v-for="s in SERVICES" :key="s.slug" :to="localePath('/services/' + s.slug + '/')" @click="close">{{ s[lang].nav }}</router-link>
      <p class="nav__drawer-h">MUSESEC</p>
      <router-link v-for="l in links" :key="l.to" :to="l.to" @click="close">{{ l.label }}</router-link>
      <router-link :to="localePath('/') + '#contact'" class="btn btn--primary" @click="close">{{ c.nav.contact }}</router-link>
      <button class="btn btn--danger" @click="close(); $emit('emergency')">{{ c.nav.emergency }}</button>
    </div>
  </header>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { content as c, lang, localePath, stripLang, saveLangPref } from '@/i18n'
import { SERVICES } from '@/content-pages.mjs'
import { SOLUTIONS } from '@/content-solutions.mjs'
import { track } from '@/track'
import BrandLogo from './BrandLogo.vue'

defineEmits(['emergency'])

const route = useRoute()
const router = useRouter()
const scrolled = ref(false)
const open = ref(false)
const drop = ref('')
const toggle = k => { drop.value = drop.value === k ? '' : k }
const links = computed(() => [
  { to: localePath('/pricing/'), label: c.value.pricingNav },
  { to: localePath('/tools/usdt-check/'), label: c.value.toolNav },
  { to: localePath('/research/'), label: c.value.researchNav },
  { to: localePath('/') + '#about', label: c.value.aboutNav },
])
const onScroll = () => { scrolled.value = window.scrollY > 10 }
const close = () => { open.value = false }

function switchLang() {
  const to = lang.value === 'zh' ? 'en' : 'zh'
  saveLangPref(to)
  track('switch_language', { label: to })
  router.push({ path: localePath(stripLang(route.path), to), hash: route.hash })
}

onMounted(() => { onScroll(); window.addEventListener('scroll', onScroll, { passive: true }) })
onUnmounted(() => window.removeEventListener('scroll', onScroll))
</script>

<style scoped>
.nav {
  position: fixed; top: 0; left: 0; right: 0; z-index: 200;
  height: var(--nav-h);
  transition: background .3s, border-color .3s, backdrop-filter .3s;
  border-bottom: 1px solid transparent;
}
.nav--solid {
  background: rgba(7, 5, 4, 0.92);
  backdrop-filter: blur(14px); -webkit-backdrop-filter: blur(14px);
  border-bottom-color: var(--ink-line);
}
.nav__inner { height: 100%; display: flex; align-items: center; gap: 32px; }
.nav__logo { display: flex; flex: none; }
.nav__links { display: flex; align-items: center; gap: 28px; margin-left: auto; }
.nav__links > a, .nav__droptoggle {
  font-family: var(--font-mono); font-size: 12px; letter-spacing: 0.18em;
  color: var(--ink-muted); position: relative; padding: 6px 0; transition: color .2s;
  white-space: nowrap;
}
.nav__lang { white-space: nowrap; }
/* 英文导航文字更长：收紧字距和间距，避免换行 */
.nav--en .nav__links { gap: 20px; }
.nav--en .nav__links > a, .nav--en .nav__droptoggle { letter-spacing: 0.1em; }
.nav__links > a::after {
  content: ''; position: absolute; left: 0; bottom: 0; height: 1px; width: 0; background: var(--accent); transition: width .25s;
}
.nav__links > a:hover, .nav__droptoggle:hover { color: var(--ink-text); }
.nav__links > a:hover::after { width: 100%; }
.caret { font-size: 9px; margin-left: 2px; }
.nav__drop { position: relative; height: var(--nav-h); display: flex; align-items: center; }
.nav__menu {
  position: absolute; top: calc(var(--nav-h) - 8px); left: -16px; min-width: 220px;
  background: rgba(15,12,10,.97); border: 1px solid var(--ink-line); padding: 8px 0;
  box-shadow: 0 18px 40px rgba(0,0,0,.4);
}
.nav__menu a { display: block; padding: 11px 18px; font-size: 14px; color: var(--ink-muted); }
.nav__menu a:hover, .nav__menu a.router-link-active { color: var(--ink-text); background: rgba(247,146,16,.08); }
.nav__actions { display: flex; align-items: center; gap: 10px; }
.nav__lang {
  font-family: var(--font-mono); font-size: 12px; letter-spacing: .1em; color: var(--ink-muted);
  height: 38px; padding: 0 12px; border: 1px solid var(--ink-line);
}
.nav__lang:hover { color: var(--ink-text); border-color: rgba(255,255,255,.3); }
.sos-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--danger); animation: blink 1.4s infinite; }
@keyframes blink { 50% { opacity: .25; } }

.nav__burger { display: none; width: 40px; height: 40px; position: relative; }
.nav__burger span { position: absolute; left: 10px; right: 10px; height: 2px; background: var(--ink-text); transition: transform .25s, top .25s; }
.nav__burger span:first-child { top: 15px; }
.nav__burger span:last-child { top: 23px; }
.nav__burger.is-open span:first-child { top: 19px; transform: rotate(45deg); }
.nav__burger.is-open span:last-child { top: 19px; transform: rotate(-45deg); }

.nav__drawer {
  position: absolute; top: var(--nav-h); left: 0; right: 0; max-height: calc(100vh - var(--nav-h)); overflow-y: auto;
  background: var(--ink); border-bottom: 1px solid var(--ink-line);
  padding: 8px var(--gutter) 24px; display: flex; flex-direction: column; gap: 2px;
}
.nav__drawer-h { font-family: var(--font-mono); font-size: 11px; letter-spacing: .2em; color: var(--accent); margin: 14px 0 4px; }
.nav__drawer > a:not(.btn) {
  font-size: 15px; color: var(--ink-muted); padding: 12px 0; border-bottom: 1px solid var(--ink-line);
}
.nav__drawer .btn { margin-top: 12px; }
.nav__drawer-grid { display: grid; grid-template-columns: 1fr 1fr; column-gap: 16px; }
.nav__drawer-grid a { font-size: 14px; color: var(--ink-muted); padding: 11px 0; border-bottom: 1px solid var(--ink-line); }

@media (max-width: 1180px) { .nav__links { gap: 18px; } }
@media (max-width: 1140px) {
  .nav--en .nav__links, .nav--en .nav__cta, .nav--en .nav__sos { display: none; }
  .nav--en .nav__actions { margin-left: auto; }
  .nav--en .nav__burger { display: block; }
}
@media (max-width: 1080px) {
  .nav__links, .nav__cta, .nav__sos { display: none; }
  .nav__actions { margin-left: auto; }
  .nav__burger { display: block; }
}
</style>
